import _ from 'lodash';

import * as api from '../../../api';

const RADIUS_OF_EARTH_MILES = 3958.8;
const RADIUS_OF_EARTH_KM = 6371.071;

type EhrTimeSlotWithSortingData = api.EhrTimeSlot.EhrTimeSlot & {
  practiceName: string;
  providerName?: string;
};

export const getLocallySortedSlots = (
  slots: api.EhrTimeSlot.EhrTimeSlot[],
  practices: Record<string, api.EhrTimeSlot.PracticeTimeSlot>,
  providers: Record<string, api.User.UserIdentification>,
  sort: api.EhrTimeSlot.Sorting,
  coordinates?: api.Places.Geometry,
): api.EhrTimeSlot.EhrTimeSlot[] => {
  const slotsWithInfo = slots.map((slot) => {
    const practice = practices[slot.practiceId];
    const { latitude, longitude, distance } = practice;
    const practiceCoordinates =
      !_.isNil(latitude) && !_.isNil(longitude)
        ? { latitude, longitude }
        : undefined;
    return {
      ...slot,
      practiceName: practice.name,
      providerName: slot.providerId
        ? providers[slot.providerId].lastName ?? undefined
        : undefined,
      distance:
        distance ??
        (coordinates && practiceCoordinates
          ? calculateDistanceBetweenCoordinates(
              coordinates,
              practiceCoordinates,
            )
          : undefined),
    };
  });
  return sortSlots(slotsWithInfo, sort);
};

const sortingFunctionMap: Record<
  api.EhrTimeSlot.Sorting,
  (
    slotA: EhrTimeSlotWithSortingData,
    slotB: EhrTimeSlotWithSortingData,
  ) => number
> = {
  [api.EhrTimeSlot.Sorting.AlphabeticallyPractice]: (
    slotA: EhrTimeSlotWithSortingData,
    slotB: EhrTimeSlotWithSortingData,
  ) => {
    return slotA.practiceName.localeCompare(slotB.practiceName);
  },
  [api.EhrTimeSlot.Sorting.AlphabeticallyProvider]: (
    slotA: EhrTimeSlotWithSortingData,
    slotB: EhrTimeSlotWithSortingData,
  ) => {
    if (!slotA.providerName) return 1;
    if (!slotB.providerName) return -1;
    return slotA.providerName.localeCompare(slotB.providerName);
  },
  [api.EhrTimeSlot.Sorting.AvailabilityPractice]: (
    slotA: EhrTimeSlotWithSortingData,
    slotB: EhrTimeSlotWithSortingData,
  ) => {
    return slotA.startTime > slotB.startTime ? 1 : -1;
  },
  [api.EhrTimeSlot.Sorting.AvailabilityProvider]: (
    slotA: EhrTimeSlotWithSortingData,
    slotB: EhrTimeSlotWithSortingData,
  ) => {
    return slotA.startTime > slotB.startTime ? 1 : -1;
  },
  [api.EhrTimeSlot.Sorting.Distance]: (
    slotA: EhrTimeSlotWithSortingData,
    slotB: EhrTimeSlotWithSortingData,
  ) => {
    if (slotA.distance === undefined) return 1;
    if (slotB.distance === undefined) return -1;
    return slotA.distance < slotB.distance ? -1 : 1;
  },
};

function sortSlots(
  ehrTimeSlots: EhrTimeSlotWithSortingData[],
  sort: api.EhrTimeSlot.Sorting,
): api.EhrTimeSlot.EhrTimeSlot[] {
  const group = _.groupBy(
    ehrTimeSlots.sort((slotA, slotB) => sortingFunctionMap[sort](slotA, slotB)),
    (slot) =>
      `${slot.practiceId}${slot.providerId ? `,${slot.providerId}` : ''}`,
  );
  return Object.values(group)
    .flat()
    .map((slot) => _.omit(slot, 'practiceName', 'providerName'));
}

// https://cloud.google.com/blog/products/maps-platform/how-calculate-distances-map-maps-javascript-api
export function calculateDistanceBetweenCoordinates(
  mk1: api.Places.Geometry,
  mk2: api.Places.Geometry,
  useMiles: boolean = true,
): number {
  const R = useMiles ? RADIUS_OF_EARTH_MILES : RADIUS_OF_EARTH_KM; // Radius of the Earth
  const rlat1 = mk1.latitude * (Math.PI / 180); // Convert degrees to radians
  const rlat2 = mk2.latitude * (Math.PI / 180); // Convert degrees to radians
  const difflat = rlat2 - rlat1; // Radian difference (latitudes)
  const difflon = (mk2.longitude - mk1.longitude) * (Math.PI / 180); // Radian difference (longitudes)

  const d =
    2 *
    R *
    Math.asin(
      Math.sqrt(
        Math.sin(difflat / 2) * Math.sin(difflat / 2) +
          Math.cos(rlat1) *
            Math.cos(rlat2) *
            Math.sin(difflon / 2) *
            Math.sin(difflon / 2),
      ),
    );
  return d;
}
